import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { 
  Tag, Plus, Search, Edit2, Trash2, Package, X, Check,
  Wrench, Laptop, Bed, Utensils, Lightbulb, Tent, Car,
  Droplet, Flame, Leaf, Music, Camera, Gamepad2, Bike, Waves,
  AlertCircle
} from 'lucide-react';
import type { Category } from '../lib/api';
import { ResultsCount } from '../components/DataTable';

const iconOptions = [
  { name: 'Wrench', icon: Wrench },
  { name: 'Laptop', icon: Laptop },
  { name: 'Bed', icon: Bed },
  { name: 'Utensils', icon: Utensils },
  { name: 'Lightbulb', icon: Lightbulb },
  { name: 'Tent', icon: Tent },
  { name: 'Car', icon: Car },
  { name: 'Droplet', icon: Droplet },
  { name: 'Flame', icon: Flame },
  { name: 'Leaf', icon: Leaf },
  { name: 'Music', icon: Music },
  { name: 'Camera', icon: Camera },
  { name: 'Gamepad2', icon: Gamepad2 },
  { name: 'Bike', icon: Bike },
  { name: 'Waves', icon: Waves }
];

const colorOptions = [
  { value: 'blue', bg: 'bg-blue-500', gradient: 'from-blue-500 to-blue-600' },
  { value: 'green', bg: 'bg-green-500', gradient: 'from-green-500 to-green-600' },
  { value: 'red', bg: 'bg-red-500', gradient: 'from-red-500 to-red-600' },
  { value: 'yellow', bg: 'bg-yellow-500', gradient: 'from-yellow-500 to-yellow-600' },
  { value: 'purple', bg: 'bg-purple-500', gradient: 'from-purple-500 to-purple-600' },
  { value: 'pink', bg: 'bg-pink-500', gradient: 'from-pink-500 to-pink-600' },
  { value: 'orange', bg: 'bg-orange-500', gradient: 'from-orange-500 to-orange-600' },
  { value: 'teal', bg: 'bg-teal-500', gradient: 'from-teal-500 to-teal-600' },
  { value: 'gray', bg: 'bg-gray-500', gradient: 'from-gray-500 to-gray-600' },
  { value: 'indigo', bg: 'bg-indigo-500', gradient: 'from-indigo-500 to-indigo-600' }
];

function getIconComponent(iconName: string) {
  const found = iconOptions.find(i => i.name === iconName);
  return found?.icon || Tag;
}

export default function Categories() {
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Listen for FAB click event from Layout
  useEffect(() => {
    const handleOpenModal = (e: CustomEvent) => {
      if (e.detail?.type === 'category') {
        setShowAddModal(true);
      }
    };
    window.addEventListener('openAddModal', handleOpenModal as EventListener);
    return () => window.removeEventListener('openAddModal', handleOpenModal as EventListener);
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: api.getCategories,
    refetchOnMount: 'always'
  });

  const deleteMutation = useMutation({
    mutationFn: api.deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    }
  });

  const filteredCategories = data?.categories.filter(
    (cat) => cat.name.toLowerCase().includes(search.toLowerCase())
  ) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-red-200 bg-red-50">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Errore: {error.message}</span>
        </div>
      </div>
    );
  }

  const handleDelete = (id: string, name: string) => {
    if (confirm(`Sei sicuro di voler eliminare "${name}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const getColorGradient = (color: string) => {
    const found = colorOptions.find((c) => c.value === color);
    return found?.gradient || 'from-gray-500 to-gray-600';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Categorie</h1>
          <p className="page-subtitle">{data?.categories.length || 0} categorie</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="btn btn-primary hidden lg:flex">
          <Plus className="w-4 h-4" />
          Nuova Categoria
        </button>
      </div>

      {/* Search Toolbar */}
      <div className="card card-sm space-y-2">
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca categorie..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input input-sm pl-8 w-full"
            />
          </div>
          {search && (
            <button onClick={() => setSearch('')} className="btn btn-ghost btn-sm flex-shrink-0">
              <X className="w-3 h-3" />
              <span className="hidden sm:inline">Reset</span>
            </button>
          )}
        </div>
        <div className="flex items-center justify-between text-xs text-gray-500 pt-1 border-t border-gray-100">
          <ResultsCount count={filteredCategories.length} label="categorie" />
        </div>
      </div>

      {/* Categories List */}
      {filteredCategories.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Tag className="empty-state-icon" />
            <p className="empty-state-title">Nessuna categoria trovata</p>
            <p className="empty-state-text">
              {search ? 'Prova a modificare la ricerca' : 'Aggiungi la tua prima categoria'}
            </p>
            {!search && (
              <button onClick={() => setShowAddModal(true)} className="btn btn-primary btn-sm mt-3">
                <Plus className="w-4 h-4" />
                Nuova Categoria
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="card card-sm">
          <div className="space-y-1">
            {filteredCategories.map((category) => {
              const IconComponent = getIconComponent(category.icon || 'Tag');
              return (
                <div 
                  key={category.id} 
                  className="flex items-center gap-3 py-2 px-2 rounded hover:bg-gray-50 cursor-pointer"
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('button')) return;
                    navigate(`/categories/${category.id}`);
                  }}
                >
                  {/* Icon */}
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${getColorGradient(category.color || 'gray')}`}>
                    <IconComponent className="w-4 h-4 text-white" />
                  </div>

                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-gray-900 text-sm">{category.name}</span>
                  </div>

                  {/* Asset count */}
                  <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                    <Package className="w-3 h-3" />
                    <span>{category.assetCount || 0}</span>
                  </div>

                  {/* Actions - always visible */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/categories/${category.id}`); }}
                      className="icon-btn icon-btn-sm"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(category.id, category.name); }}
                      className="icon-btn icon-btn-sm hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <CategoryModal
          category={null}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}

function CategoryModal({
  category,
  onClose
}: {
  category: Category | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = !!category;
  const [name, setName] = useState(category?.name || '');
  const [icon, setIcon] = useState(category?.icon || 'Wrench');
  const [color, setColor] = useState(category?.color || 'blue');

  const createMutation = useMutation({
    mutationFn: api.createCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      onClose();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      onClose();
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data = { name, icon, color };

    if (isEdit) {
      updateMutation.mutate({ id: category.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  const SelectedIcon = getIconComponent(icon);
  const selectedColorGradient = colorOptions.find(c => c.value === color)?.gradient || 'from-gray-500 to-gray-600';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isEdit ? 'Modifica Categoria' : 'Nuova Categoria'}
          </h2>
          <button onClick={onClose} className="icon-btn icon-btn-sm">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Preview */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Anteprima</p>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-gradient-to-br ${selectedColorGradient}`}>
              <SelectedIcon className="w-5 h-5 text-white" />
            </div>
            <span className="font-medium text-gray-900">{name || 'Nome categoria'}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            <div className="form-group">
              <label className="form-label">Nome *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="es. Attrezzatura sportiva"
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="form-label">Icona</label>
              <div className="grid grid-cols-5 gap-2">
                {iconOptions.map((iconOpt) => {
                  const IconComp = iconOpt.icon;
                  const isSelected = icon === iconOpt.name;
                  return (
                    <button
                      key={iconOpt.name}
                      type="button"
                      onClick={() => setIcon(iconOpt.name)}
                      className={`p-2 rounded-lg border transition-all flex items-center justify-center ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 text-primary-600'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <IconComp className="w-4 h-4" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Colore</label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map((colorOption) => (
                  <button
                    key={colorOption.value}
                    type="button"
                    onClick={() => setColor(colorOption.value)}
                    className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorOption.gradient} transition-all flex items-center justify-center ${
                      color === colorOption.value
                        ? 'ring-2 ring-offset-1 ring-primary-500 scale-110'
                        : 'hover:scale-105'
                    }`}
                  >
                    {color === colorOption.value && (
                      <Check className="w-4 h-4 text-white" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <p className="text-red-600 text-xs bg-red-50 p-2 rounded">
                {error.message}
              </p>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Annulla
            </button>
            <button
              type="submit"
              disabled={isPending || !name.trim()}
              className="btn btn-primary"
            >
              {isPending ? (
                <>
                  <div className="spinner spinner-sm" />
                  Salvataggio...
                </>
              ) : isEdit ? (
                'Salva'
              ) : (
                'Crea'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
